import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase-client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Search, Download, Users, ChevronRight } from 'lucide-react';

interface Candidate {
  id: string;
  interviewSessionId: string | null;
  fullName: string;
  email: string;
  jobTitle: string;
  jobId: string | null;
  interviewDate: string | null;
  status: 'completed' | 'in-progress' | 'scheduled';
  finalScore: number | null;
  communicationScore: number | null;
  technicalScore: number | null;
}

export default function CandidatesPage() {
  const navigate = useNavigate();
  const { getOrganizationId, isRecruiter, isRecruiterValue, organizationId } = useAuth();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [filteredCandidates, setFilteredCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!isRecruiterValue) {
      navigate('/dashboard');
      return;
    }

    const fetchCandidates = async () => {
      if (!organizationId) {
        setLoading(false);
        return;
      }

      try {
        const { data: jobsData, error: jobsError } = await supabase
          .from('job_postings')
          .select('id, title')
          .eq('organization_id', organizationId);

        if (jobsError) throw jobsError;

        // Fetch candidate profiles
        // Note: RLS policies automatically filter this query to only return:
        // 1. Candidates with is_open_to_opportunities = true (talent pool), OR
        // 2. Candidates who have interviewed for this organization's jobs
        // No additional filtering needed here - the database handles privacy controls
        const { data: candidateProfiles, error: candidateError } = await supabase
          .from('user_profiles')
          .select('id, full_name')
          .eq('is_recruiter', false);

        if (candidateError) throw candidateError;

        const { data: usersData, error: usersError } = await supabase.auth.admin.listUsers();

        const candidatesList: Candidate[] = await Promise.all(
          (candidateProfiles || []).map(async (profile) => {
            const { data: sessions } = await supabase
              .from('interview_sessions')
              .select('id, job_posting_id, created_at, ai_evaluation, status, candidate_email')
              .eq('user_id', profile.id)
              .eq('status', 'completed')
              .order('created_at', { ascending: false });

            const userEmail = usersData?.users?.find((u: any) => u.id === profile.id)?.email || 'N/A';

            // Calculate average scores across all sessions for this organization
            let finalScore = null;
            let communicationScore = null;
            let technicalScore = null;

            const organizationSessions = sessions?.filter(s =>
              jobsData?.some(j => j.id === s.job_posting_id)
            ) || [];

            if (organizationSessions.length > 0) {
              const scores = organizationSessions
                .map(s => s.ai_evaluation?.finalScore || s.ai_evaluation?.overall_score)
                .filter(s => s != null);

              if (scores.length > 0) {
                finalScore = scores.reduce((a, b) => a + b, 0) / scores.length;
              }

              const commScores = organizationSessions
                .map(s => s.ai_evaluation?.communication)
                .filter(s => s != null);
              if (commScores.length > 0) {
                communicationScore = commScores.reduce((a, b) => a + b, 0) / commScores.length;
              }

              const techScores = organizationSessions
                .map(s => s.ai_evaluation?.technicalKnowledge)
                .filter(s => s != null);
              if (techScores.length > 0) {
                technicalScore = techScores.reduce((a, b) => a + b, 0) / techScores.length;
              }
            }

            const latestSession = organizationSessions[0];
            const status: 'completed' | 'in-progress' | 'scheduled' = latestSession ? 'completed' : 'scheduled';

            return {
              id: profile.id,
              interviewSessionId: latestSession?.id || null,
              fullName: profile.full_name || 'Anonymous',
              email: userEmail,
              jobTitle: organizationSessions.length > 0
                ? `${organizationSessions.length} interview${organizationSessions.length !== 1 ? 's' : ''}`
                : 'Not yet interviewed',
              jobId: null,
              interviewDate: latestSession?.created_at || null,
              status,
              finalScore,
              communicationScore,
              technicalScore,
            };
          })
        );

        setCandidates(candidatesList);
        setFilteredCandidates(candidatesList);
      } catch (error) {
        console.error('Error fetching candidates:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCandidates();
  }, [getOrganizationId, isRecruiter, navigate]);

  useEffect(() => {
    let filtered = [...candidates];

    if (searchTerm) {
      filtered = filtered.filter(
        (candidate) =>
          candidate.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          candidate.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          candidate.jobTitle.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredCandidates(filtered);
  }, [searchTerm, candidates]);

  const handleExportCSV = () => {
    const headers = ['Name', 'Email', 'Job Title', 'Interview Date', 'Status', 'Final Score'];
    const rows = filteredCandidates.map((candidate) => [
      candidate.fullName,
      candidate.email,
      candidate.jobTitle,
      candidate.interviewDate ? new Date(candidate.interviewDate).toLocaleDateString() : 'N/A',
      candidate.status,
      candidate.finalScore ? `${candidate.finalScore.toFixed(1)}%` : 'N/A',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `candidates_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading candidates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Candidates</h1>
          <p className="text-gray-600">View and manage candidates who interviewed for your jobs</p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Filter Candidates</CardTitle>
            <CardDescription>Search and filter candidates by various criteria</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by name, email, or job..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {filteredCandidates.length} of {candidates.length} candidates
              </p>
              <Button onClick={handleExportCSV} variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            {filteredCandidates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Users className="w-16 h-16 text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No candidates found</h3>
                <p className="text-gray-600 text-center max-w-md">
                  {candidates.length === 0
                    ? 'No candidates have interviewed for your jobs yet.'
                    : 'Try adjusting your filters to see more candidates.'}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Candidate</TableHead>
                    <TableHead>Interviews</TableHead>
                    <TableHead>Latest Interview</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Avg Score</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCandidates.map((candidate) => (
                    <TableRow
                      key={candidate.interviewSessionId || candidate.id}
                      className="hover:bg-gray-50"
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium text-gray-900">{candidate.fullName}</p>
                          <p className="text-sm text-gray-600">{candidate.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {candidate.jobTitle !== 'Not yet interviewed' ? (
                          <button
                            onClick={() => navigate(`/interviews?candidateId=${candidate.id}`)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium transition-colors"
                          >
                            {candidate.jobTitle}
                          </button>
                        ) : (
                          <span className="text-gray-600">{candidate.jobTitle}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {candidate.interviewDate ? (
                          new Date(candidate.interviewDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        ) : (
                          <span className="text-gray-400">Not interviewed</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            candidate.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : candidate.status === 'in-progress'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {candidate.status.charAt(0).toUpperCase() + candidate.status.slice(1)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {candidate.finalScore !== null ? (
                          <span className="font-semibold text-green-600">
                            {candidate.finalScore.toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {candidate.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/profile/${candidate.id}`);
                              }}
                            >
                              View Profile
                              <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
